import { useEffect, useState } from "react";

import type { AdminListPagination, AdminStoreDetailResponseData, AdminStoreSummary } from "@chaochud/contracts";

import { AuditTrail, CriticalActionForm, DataTable, PageHeader, StatusPill, toAuditTrailEntry } from "@/components/admin-ui";
import { adminOpsClient } from "@/lib/api-clients";
import type { AuditTrailEntry } from "@/components/admin-ui";

const STATUS_TONE: Record<string, "success" | "warning" | "error" | "neutral"> = {
  ACTIVE: "success",
  SUSPENDED: "error",
  VERIFIED: "success",
  PENDING: "warning",
  UNVERIFIED: "neutral",
};

export function StoresPage() {
  const [items, setItems] = useState<AdminStoreSummary[]>([]);
  const [pagination, setPagination] = useState<AdminListPagination | null>(null);
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<AdminStoreDetailResponseData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [auditEntries, setAuditEntries] = useState<AuditTrailEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const load = async (targetPage = page) => {
    const data = await adminOpsClient.listStores(targetPage);
    setItems(data.items);
    setPagination(data.pagination);
  };

  useEffect(() => {
    void load(page).catch((err) => setError(err instanceof Error ? err.message : "error"));
  }, [page]);

  useEffect(() => {
    if (!detail) {
      setAuditEntries([]);
      return;
    }
    setAuditLoading(true);
    adminOpsClient
      .listResourceAuditLogs("STORE", detail.id)
      .then((data) => setAuditEntries(data.items.map(toAuditTrailEntry)))
      .catch(() => setAuditEntries([]))
      .finally(() => setAuditLoading(false));
  }, [detail]);

  return (
    <div className="space-y-6">
      <PageHeader title="จัดการร้านค้า" />
      {error ? <p className="text-sm text-error-text">{error}</p> : null}
      <DataTable
        headers={["ชื่อ", "Slug", "สถานะ", "Verify", "Plan", ""]}
        rows={items.map((store) => [
          store.name,
          store.slug,
          <StatusPill key={`status-${store.id}`} tone={STATUS_TONE[store.status] ?? "neutral"}>
            {store.status}
          </StatusPill>,
          <StatusPill key={`verify-${store.id}`} tone={STATUS_TONE[store.verificationStatus] ?? "neutral"}>
            {store.verificationStatus}
          </StatusPill>,
          store.currentPlanCode,
          <button
            key={store.id}
            type="button"
            className="text-sm font-semibold text-plum hover:underline"
            onClick={async () => {
              try {
                setDetail(await adminOpsClient.getStore(store.id));
              } catch (err) {
                setError(err instanceof Error ? err.message : "error");
              }
            }}
          >
            เปิด
          </button>,
        ])}
        pagination={pagination ?? undefined}
        onPageChange={setPage}
      />
      {detail ? (
        <div className="grid gap-4 md:grid-cols-[1fr_280px]">
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-line bg-slate-surface p-4 text-sm text-ink">
              <p className="font-medium">{detail.name}</p>
              <p>Owner: {detail.ownerEmail ?? detail.ownerUserId}</p>
              <p>
                Status: {detail.status} · Verify: {detail.verificationStatus}
              </p>
            </div>
            <CriticalActionForm
              title="ยืนยันร้าน"
              targetLabel={detail.name}
              currentState={detail.verificationStatus}
              expectedState="VERIFIED"
              impact="ร้านผ่านการตรวจสอบจากแอดมิน"
              submitLabel="Verify"
              onSubmit={async (payload) => {
                setDetail(await adminOpsClient.verifyStore(detail.id, payload));
                await load();
              }}
            />
            {detail.status !== "SUSPENDED" ? (
              <CriticalActionForm
                title="ระงับร้าน"
                targetLabel={detail.name}
                currentState={detail.status}
                expectedState="SUSPENDED"
                impact="ร้านจะรับจองใหม่ไม่ได้"
                submitLabel="Suspend store"
                onSubmit={async (payload) => {
                  setDetail(await adminOpsClient.suspendStore(detail.id, payload));
                  await load();
                }}
              />
            ) : (
              <CriticalActionForm
                title="เปิดร้านอีกครั้ง"
                targetLabel={detail.name}
                currentState={detail.status}
                expectedState="ACTIVE"
                impact="ร้านกลับมาใช้งานได้"
                submitLabel="Unsuspend store"
                onSubmit={async (payload) => {
                  setDetail(await adminOpsClient.unsuspendStore(detail.id, payload));
                  await load();
                }}
              />
            )}
          </div>
          <AuditTrail entries={auditEntries} isLoading={auditLoading} />
        </div>
      ) : null}
    </div>
  );
}
